import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/home/HeroSection';
import AdvisorCTA from '../components/home/AdvisorCTA';
import { LogoFull } from '../components/Logo';
import GlobalNav from '../components/GlobalNav';
import useInView from '../hooks/useInView';

const CATEGORY_ACCENTS = {
  'CPA': '#a8f0c6', 'CPA / Accountant': '#a8f0c6',
  'Payroll': '#ffd97d', 'Payroll Specialist': '#ffd97d',
  'Banker': '#b8d4ff', 'Business Banker': '#b8d4ff',
  'HR': '#f9c5d1', 'HR Professional': '#f9c5d1',
  'Insurance': '#d4b8ff', 'Insurance Advisor': '#d4b8ff',
  'Retirement': '#ffc9a0', 'Retirement Planner': '#ffc9a0',
};

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/api/photos/')) return url;
  return `/api/upload/${url.replace(/^\/?(api\/upload\/?)+/, '')}`;
}

function ShowcaseCard({ a }) {
  const [imgFailed, setImgFailed] = useState(false);
  const name = ((a.first_name || '') + ' ' + (a.last_name || '')).trim() || 'Advisor';
  const type = a.category_name || 'Advisor';
  const accent = CATEGORY_ACCENTS[type] || '#a8f0c6';
  const photo = resolvePhoto(a.profile_photo_url || a.profile_photo);
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const rate = a.hourly_rate ? `$${Math.round(parseFloat(a.hourly_rate))}/hr` : null;
  const bookingLink = a.booking_link || a.calendly_url;
  const slug = a.profile_slug || a.id;
  const city = a.location ? a.location.split(',')[0] : '';
  const yearsStr = a.years_experience > 0 ? ` · ${a.years_experience} yrs` : '';

  return (
    <Link to={`/advisor/${slug}`} style={{ textDecoration: 'none' }}>
      <div
        style={{
          background: '#fff', borderRadius: 16,
          border: '1.5px solid #e8e5e0',
          padding: '24px 22px',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = '#004D35';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,77,53,0.1)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = '#e8e5e0';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {photo && !imgFailed ? (
            <img
              src={photo}
              alt={name}
              onError={() => setImgFailed(true)}
              style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div style={{
              width: 44, height: 44, borderRadius: '50%', background: accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 700, color: '#004D35',
              fontFamily: 'Georgia, serif', flexShrink: 0,
            }}>
              {initials}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0a1a0f', fontFamily: 'Georgia, serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </div>
            <div style={{ fontSize: 11, color: '#777', fontFamily: 'sans-serif', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span style={{ color: '#004D35', fontWeight: 700 }}>✓</span>{' '}{type}{yearsStr}{city ? ` · ${city}` : ''}
            </div>
          </div>
        </div>

        {a.headline && (
          <p style={{
            fontSize: 13, color: '#666', fontFamily: 'sans-serif',
            lineHeight: 1.55, margin: '0 0 14px', flex: 1,
          }}>
            {a.headline.length > 110 ? a.headline.slice(0, 110) + '\u2026' : a.headline}
          </p>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f0ede8',
          flexWrap: 'wrap', gap: 6,
        }}>
          {rate && (
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0a1a0f', fontFamily: 'Georgia, serif' }}>
              {rate}
            </span>
          )}
          {bookingLink && (
            <span style={{
              fontSize: 11, fontFamily: 'sans-serif', color: '#004D35',
              fontWeight: 600, background: 'rgba(0,77,53,0.07)',
              padding: '3px 10px', borderRadius: 999,
            }}>
              📅 Free intro call
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function AdvisorShowcase() {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sectionRef, inView] = useInView(0.1);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    fetch('/api/advisors/search?limit=100')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.advisors) return;
        const sorted = [...d.advisors].sort((a, b) => {
          const score = x =>
            ((x.profile_photo_url || x.profile_photo) ? 2 : 0) +
            ((x.booking_link || x.calendly_url) ? 1 : 0);
          return score(b) - score(a);
        });
        setAdvisors(sorted.slice(0, 4));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const showGrid = !loading && advisors.length >= 2;

  return (
    <section ref={sectionRef} style={{
      background: '#f5f3ef',
      padding: isMobile ? '72px 24px 80px' : '100px 40px 120px',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: showGrid ? 52 : 36 }}>
          <p style={{
            fontSize: 11, fontFamily: 'sans-serif', letterSpacing: '0.2em',
            color: '#004D35', fontWeight: 700, textTransform: 'uppercase', marginBottom: 16,
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}>
            ◆ THE ADVISORS
          </p>
          <h2 style={{
            fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: 400,
            fontFamily: 'Georgia, serif', color: '#0a1a0f', lineHeight: 1.05, marginBottom: 16,
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
          }}>
            Browse <em style={{ color: '#004D35' }}>verified</em> advisors.
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 17, fontFamily: 'sans-serif', color: '#666',
            maxWidth: 540, margin: 0,
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
          }}>
            Every profile reviewed and verified by our team before it goes live. No pay-to-rank, no ads.
          </p>
        </div>

        {showGrid && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
            marginBottom: 48,
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease 0.3s, transform 0.6s ease 0.3s',
          }}>
            {advisors.map(a => <ShowcaseCard key={a.id} a={a} />)}
          </div>
        )}

        <div style={{
          textAlign: 'center',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.6s ease 0.45s, transform 0.6s ease 0.45s',
        }}>
          <a
            href="/browse"
            style={{
              background: '#004D35', color: '#fff',
              padding: '15px 36px', borderRadius: 999,
              fontSize: 15, fontWeight: 600, fontFamily: 'sans-serif',
              textDecoration: 'none', display: 'inline-block',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#003d2a'}
            onMouseLeave={e => e.currentTarget.style.background = '#004D35'}
          >
            Browse all advisors →
          </a>
        </div>
      </div>
    </section>
  );
}

const Footer = () => {
  return (
    <footer className="bg-white border-t border-border-light py-8">
      <div className="max-w-site mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoFull size={28} />
            <span className="text-text-muted text-sm">© 2026</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-text-secondary">
            <Link to="/privacy" className="hover:text-text-primary transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Privacy</Link>
            <Link to="/terms" className="hover:text-text-primary transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Terms</Link>
            <Link to="/contact" className="hover:text-text-primary transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-bg-main">
      <GlobalNav page="homepage" />

      <main>
        <HeroSection />
        <AdvisorShowcase />
        <AdvisorCTA />
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
